import { useState, useCallback, useEffect } from 'react';

// Zebra Vendor ID
const ZEBRA_VENDOR_ID = 0x0a5f;

// Basic type declarations for when @types/w3c-web-usb is missing
interface ZebraUSBDevice {
  device: any; // USBDevice
  interfaceNumber: number;
  endpointIn: number;
  endpointOut: number;
}

export const useZebraWebUSB = () => {
  const [device, setDevice] = useState<ZebraUSBDevice | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Function to find the correct interface and endpoints for Zebra
  const setupDevice = async (usbDevice: any): Promise<ZebraUSBDevice> => {
    console.log('Iniciando configuración USB para:', usbDevice.productName);
    await usbDevice.open();
    await usbDevice.selectConfiguration(1);
    
    console.log('Configuración actual:', usbDevice.configuration);
    
    // Find the first interface with a bulk-out endpoint
    let interfaceNumber = -1;
    let endpointOut = -1;
    let endpointIn = -1;

    if (usbDevice.configuration) {
      for (const iface of usbDevice.configuration.interfaces) {
        console.log(`Inspeccionando interfaz ${iface.interfaceNumber}`, iface);
        for (const alt of iface.alternates) {
          console.log(`  Alternate: class=${alt.interfaceClass}, protocol=${alt.interfaceProtocol}`);
          // Typical printer interface (Class 7) or custom Zebra class
          if (alt.interfaceClass === 7 || alt.interfaceClass === 255) { 
            interfaceNumber = iface.interfaceNumber;
            for (const ep of alt.endpoints) {
              console.log(`    Endpoint: ${ep.endpointNumber}, type=${ep.type}, dir=${ep.direction}`);
              if (ep.direction === 'out' && ep.type === 'bulk') endpointOut = ep.endpointNumber;
              if (ep.direction === 'in' && ep.type === 'bulk') endpointIn = ep.endpointNumber;
            }
          }
        }
      }

      if (interfaceNumber === -1 || endpointOut === -1) {
        console.warn('No se detectó clase 7 (Printer), intentando fallback a interfaz 0');
        interfaceNumber = 0;
        const alt = usbDevice.configuration.interfaces[0].alternates[0];
        for (const ep of alt.endpoints) {
          if (ep.direction === 'out' && ep.type === 'bulk') endpointOut = ep.endpointNumber;
          if (ep.direction === 'in' && ep.type === 'bulk') endpointIn = ep.endpointNumber;
        }
      }
    }

    console.log(`Reclamando interfaz ${interfaceNumber}, EndpointOut: ${endpointOut}`);
    await usbDevice.claimInterface(interfaceNumber);
    console.log('Interfaz reclamada con éxito');
    
    return {
      device: usbDevice,
      interfaceNumber,
      endpointIn,
      endpointOut
    };
  };

  const connect = async () => {
    const nav: any = navigator;
    if (!nav.usb) {
        setError('Tu navegador no soporta WebUSB. Usa Chrome o Edge.');
        return false;
    }

    setLoading(true);
    setError(null);
    try {
      const selectedDevice = await nav.usb.requestDevice({
        filters: [{ vendorId: ZEBRA_VENDOR_ID }]
      });
      const zebra = await setupDevice(selectedDevice);
      setDevice(zebra);
      // Save product ID or some identifier to localStorage if needed for auto-connect
      localStorage.setItem('zebra_usb_paired', 'true');
      return true;
    } catch (err: any) {
      console.error('USB Connection Error:', err);
      // If user cancelled, don't show as error
      if (err.name === 'NotFoundError') return false;
      setError(err.message || 'No se pudo conectar con la impresora');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const disconnect = useCallback(async () => {
    if (device) {
      try {
        await device.device.releaseInterface(device.interfaceNumber);
        await device.device.close();
      } catch (e) {
        console.warn('Disconnect error:', e);
      }
      setDevice(null);
    }
  }, [device]);

  const printZPL = async (zpl: string) => {
    if (!device) {
      setError('Impresora no conectada');
      return false;
    }

    setLoading(true);
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(zpl);
      await device.device.transferOut(device.endpointOut, data);
      return true;
    } catch (err: any) {
      console.error('Print Error:', err);
      setError('Error al enviar datos: ' + err.message);
      
      // If device is lost, reset state
      if (err.name === 'NetworkError') {
        setDevice(null);
      }
      
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Auto-connect attempt if previously paired
  useEffect(() => {
    const checkPaired = async () => {
      const nav: any = navigator;
      if (nav.usb && localStorage.getItem('zebra_usb_paired') === 'true') {
        try {
          const devices = await nav.usb.getDevices();
          const zebra = devices.find((d: any) => d.vendorId === ZEBRA_VENDOR_ID);
          if (zebra) {
            const setup = await setupDevice(zebra);
            setDevice(setup);
          }
        } catch (e) {
          console.warn('Auto-connect failed', e);
        }
      }
    };
    checkPaired();
  }, []);

  return {
    connect,
    disconnect,
    printZPL,
    isConnected: !!device,
    loading,
    error,
    deviceName: device?.device.productName || null
  };
};

